/**
 * @ngdoc service
 * @name ims.map.service:RFTransmitterProcessor
 * @description
 * RFTransmitterProcessor objects are for use in an RFPlanProcessor.
 * They are used to generate an RF Coverage Map.
 */

import LatLonSpherical from 'geodesy/latlon-spherical';
import { Cesium } from 'app/cesium';
import { antFileStringToArray } from 'app/views/view_types/network/features/rf_planning/rf_utils';
import { COVERAGE_PLAN_TYPE } from 'app/views/view_types/network/features/rf_planning/collections/rf_plan_coverage_types';
import { RF_TRANSMITTER_TYPE_OPTIONS } from 'app/views/view_types/network/features/rf_planning/collections/rf_transmitter_type_options';

// RFTransmitterProcessor object. Used as input to calculate coverage
export class RFTransmitterProcessor {
    private _latitude: number;
    private _longitude: number;
    private _latitudeRadians: number;
    private _longitudeRadians: number;
    private _cartographic;

    public initialGroundHeight: number;
    public groundHeight: number;
    public frequencyMhz: number;
    public txPowerdBm: number;
    public txAntennaGain: number;
    public txHeightAboveGround: number;
    public rxAntennaGain: number;
    public rxHeightAboveGround: number;
    public type: string = 'Omnidirectional'; // "Omnidirectional", "Beam" or "ANT File"
    public verticalAngleDeg: number;
    public horizontalAngleDeg: number;
    public beamWidthDeg: number;
    public beamHeightDeg: number;
    public antFile: Array<string>;

    public minLatitude: number;
    public maxLatitude: number;
    public minLongitude: number;
    public maxLongitude: number;
    public minGridX: number;
    public minGridY: number;
    public maxGridX: number;
    public maxGridY: number;
    public gridWidth: number;
    public gridHeight: number;
    public gridCellWidth: number;
    public gridCellHeight: number;
    public gridCellWidthMeters: number;
    public gridCellHeightMeters: number;
    public coveragePlanType: COVERAGE_PLAN_TYPE;
    public frequencyColor: number[];
    public minRssiCutoff: number;
    public positionsGrid: any[][];
    public heightArray: Float32Array;
    public outputArray: Int32Array;
    public maxBroadcastDistance: number;

    constructor(
        latitude: number,
        longitude: number,
        txHeightAboveGround: number,
        groundHeight: number = null,
        frequencyMhz: number = 2437,
        txPowerdBm: number = 20,
        txAntennaGain: number = 0,
        rxAntennaGain: number = 0,
        rxHeightAboveGround: number = 1,
        minRssiCutoff: number = -80,
        type: string = RF_TRANSMITTER_TYPE_OPTIONS.omnidirectional,
        verticalAngleDeg: number = 0,
        horizontalAngleDeg: number = 0,
        beamWidthDeg: number = 360,
        beamHeightDeg: number = 180,
        antFileString: string,
    ) {
        this._latitude = latitude;
        this._longitude = longitude;
        this.setPosition(latitude, longitude);

        this.initialGroundHeight = groundHeight;
        this.groundHeight = this.initialGroundHeight;
        this.frequencyMhz = frequencyMhz;
        this.txAntennaGain = txAntennaGain;
        this.txHeightAboveGround = txHeightAboveGround;
        this.rxAntennaGain = rxAntennaGain;
        this.rxHeightAboveGround = rxHeightAboveGround;
        this.txPowerdBm = txPowerdBm;
        this.type = type;
        this.verticalAngleDeg = verticalAngleDeg;
        this.horizontalAngleDeg = horizontalAngleDeg;
        this.beamWidthDeg = beamWidthDeg;
        this.beamHeightDeg = beamHeightDeg;
        this.antFile = antFileString ? antFileStringToArray(antFileString) : undefined;

        // Bounds (calculated)
        this.minLatitude = null;
        this.maxLatitude = null;
        this.minLongitude = null;
        this.maxLongitude = null;

        // Location and dimensions on coverage map grid (calculated)
        this.minGridX = null;
        this.minGridY = null;
        this.maxGridX = null;
        this.maxGridY = null;
        this.gridWidth = null;
        this.gridHeight = null;
        this.gridCellWidth = null;
        this.gridCellHeight = null;
        this.gridCellWidthMeters = null; // in meters
        this.gridCellHeightMeters = null; // in meters

        // The type of coverage plan to be generated
        this.coveragePlanType = null;

        // The colour for this frequency, set by the RFPlanProcessor
        this.frequencyColor = null;

        // Minimum RSSI that is drawn
        this.minRssiCutoff = minRssiCutoff;

        // Sample positions (calculated)
        this.positionsGrid = null;

        // Array of position height (Float32Array)
        this.heightArray = null;

        // Array of RSSI per grid point
        this.outputArray = null;

        // Max broadcast distance (calculated)
        this.maxBroadcastDistance = 0;
    } // Latitude in degrees // Longitude in degrees // Ground height of the base of the RF Transmitter // RF Frequency // Transmit power in milliwatts. If null, will use dBm // Transmit ower in dBm. Default = 20dBm. Specifying overrides specified milliwatts // The height of the transmitter above ground level (also known as mast height) // Transmitter antenna gain // The receiver antenna gain // The receiver height above ground (also known as mast height)

    // Calculates the latitude/longitude bounds of this from max bradcast distance
    calculateBoundingLatLons() {
        const latLon = new LatLonSpherical(this._latitude, this._longitude);
        this.maxLongitude = latLon.destinationPoint(this.maxBroadcastDistance, 90).lon;
        this.minLatitude = latLon.destinationPoint(this.maxBroadcastDistance, 180).lat;
        this.minLongitude = latLon.destinationPoint(this.maxBroadcastDistance, 270).lon;
        this.maxLatitude = latLon.destinationPoint(this.maxBroadcastDistance, 0).lat;
    }

    get longitude() {
        return this._longitude;
    }

    get latitude() {
        return this._latitude;
    }

    get cartographic() {
        return this._cartographic;
    }

    // Sets max broadcast distance in meters
    calcMaxBroadcastDistance(minRssi) {
        const FSPL = this.txPowerdBm + this.txAntennaGain + this.rxAntennaGain - minRssi;
        const a = FSPL - (32.44 + 20 * Math.log10(this.frequencyMhz));
        const distance = Math.pow(10, a / 20);

        this.maxBroadcastDistance = distance * 1000;
        this.calculateBoundingLatLons();
    }

    calcGridReference(
        minMapLon,
        minMapLat,
        mapResX,
        mapResY,
        mapWidth,
        mapHeight,
        gridCellWidth,
        gridCellHeight,
        gridCellWidthMeters,
        gridCellHeightMeters,
    ) {
        if (
            !(
                mapResX &&
                mapResY &&
                mapWidth &&
                mapHeight &&
                gridCellWidth &&
                gridCellHeight &&
                gridCellWidthMeters &&
                gridCellHeightMeters
            )
        ) {
            throw new Error('Please select different RF parameters');
        }

        const minDistX = this.minLongitude - minMapLon;
        const minDistY = this.minLatitude - minMapLat;
        const maxDistX = this.maxLongitude - minMapLon;
        const maxDistY = this.maxLatitude - minMapLat;

        this.minGridX = Math.floor((minDistX / mapWidth) * mapResX);
        this.minGridY = Math.floor((minDistY / mapHeight) * mapResY);
        this.maxGridX = Math.floor((maxDistX / mapWidth) * mapResX);
        this.maxGridY = Math.floor((maxDistY / mapHeight) * mapResY);
        this.gridWidth = this.maxGridX - this.minGridX;
        this.gridHeight = this.maxGridY - this.minGridY;
        this.gridCellWidth = gridCellWidth;
        this.gridCellHeight = gridCellHeight;
        this.gridCellWidthMeters = gridCellWidthMeters;
        this.gridCellHeightMeters = gridCellHeightMeters;
    }

    setPosition(latDeg, lngDeg) {
        this._latitude = latDeg;
        this._longitude = lngDeg;
        this._latitudeRadians = Cesium.Math.toRadians(latDeg);
        this._longitudeRadians = Cesium.Math.toRadians(lngDeg);
        this._cartographic = new Cesium.Cartographic(this._longitudeRadians, this._latitudeRadians);
    }

    calcSamplePositions(
        minMapLon: number,
        minMapLat: number,
        positionsToSample: any[],
        mainPositionGrid: any[],
    ) {
        const transmitterCartesianSeaLevelPos = new Cesium.Cartesian3.fromRadians(
            this._longitudeRadians,
            this._latitudeRadians,
        );

        // Squared distance is faster to calculate
        const maxBroadcastDistanceSquared = Math.pow(this.maxBroadcastDistance, 2);

        // Initialise object here so a new object isn't created in the for loop for distance testing
        const seaLevelCartPos = new Cesium.Cartesian3();

        // Work with radians in this case as it's slightly faster
        const minMapLatRad = Cesium.Math.toRadians(minMapLat);
        const minMapLonRad = Cesium.Math.toRadians(minMapLon);
        const gridCellWidthRad = Cesium.Math.toRadians(this.gridCellWidth);
        const gridCellHeightRad = Cesium.Math.toRadians(this.gridCellHeight);

        // scratch variables to save memory
        let x, y, xPos, yPos, calculatedPosition, broadcastDistanceSquared;

        // TODO Does each transmitter processor needs it's own positionsGrid, if it's checking to see if it's in the main grid already?
        this.positionsGrid = new Array(this.gridHeight);
        for (y = 0; y < this.gridHeight; y++) {
            this.positionsGrid[y] = new Array(this.gridWidth);
            yPos = minMapLatRad + (y + this.minGridY) * gridCellHeightRad;
            for (x = 0; x < this.gridWidth; x++) {
                xPos = minMapLonRad + (x + this.minGridX) * gridCellWidthRad;
                Cesium.Cartesian3.fromRadians(
                    xPos,
                    yPos,
                    0,
                    Cesium.Ellipsoid.WGS84,
                    seaLevelCartPos,
                );

                broadcastDistanceSquared = Cesium.Cartesian3.distanceSquared(
                    seaLevelCartPos,
                    transmitterCartesianSeaLevelPos,
                );
                if (broadcastDistanceSquared < maxBroadcastDistanceSquared) {
                    calculatedPosition = mainPositionGrid[this.minGridY + y][this.minGridX + x];
                    if (!calculatedPosition) {
                        calculatedPosition = new Cesium.Cartographic(xPos, yPos, 0);
                        positionsToSample.push(calculatedPosition);
                        mainPositionGrid[this.minGridY + y][this.minGridX + x] = calculatedPosition;
                    }
                    this.positionsGrid[y][x] = calculatedPosition;
                } else {
                    this.positionsGrid[y][x] = 0;
                }
            }
        }
    }

    calcCoverage() {}

    create2DHeightArray() {
        // Create Float32Array with width
        this.heightArray = new Float32Array(this.gridHeight * this.gridWidth);
        this.outputArray = new Int32Array(this.gridHeight * this.gridWidth);

        let pos, x;
        for (let y = 0; y < this.gridHeight; y++) {
            for (x = 0; x < this.gridWidth; x++) {
                pos = this.positionsGrid[y][x];
                // Use height if available, otherwise default to -20000.0
                this.heightArray[y * this.gridWidth + x] = pos ? pos.height : -20000.0;
            }
        }
    }
}
